import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as chatActions from 'react/redux/actions/chat';

import { ChatPremadeResponsesComponent } from './ChatPremadeResponsesComponent.jsx';

import { Request } from 'components/Request.jsx';

import EmojiPicker from 'react/elements/EmojiPicker';
import { ChatMessageNotification } from "models/ChatMessage";
import { __ } from 'i18n/index';
import LfgComposerButton from 'react/chat/inner/display_pieces/LfgComposerButton';
import { isNetplayPageActive } from 'react/netplay/netplayPageState';
import { loadComposerEditor, loadedComposerEditor } from 'react/chat/composer/loadComposerEditor';

// The message composer: a Lexical editor that draws emoji, mentions, room
// links and guide links as chips (react/chat/composer) and sends plain text.
// The editor is its own chunk; until it loads, a read-only stand-in shows the
// placeholder and the draft is kept in state.
class ChatInputGroupComponent extends Component {
	static defaultProps = {
		shouldFocusOnMount: true
	};

	constructor(props) {
		super(props);
		const { chatMessages } = this.props;
		// Kept on the chat so a draft survives the composer remounting.
		const savedState = chatMessages.componentState;
		this.state = {
			inputValue: savedState ? savedState.inputValue : '',
			emojiPickerOpen: false,
			ComposerEditor: loadedComposerEditor(),
		};
		chatMessages.setChatInputComponent(this);

		this.composer = React.createRef();
		this.chatInputReference = null;
		this.isTyping = false;

		this.emojiTriggerRef = React.createRef();
		this.closeEmojiPicker = this.closeEmojiPicker.bind(this);
		this.addEmoji = this.addEmoji.bind(this);
		this.toggleEmojiPicker = this.toggleEmojiPicker.bind(this);
		this.performSendChatAction = this.performSendChatAction.bind(this);
		this.editLastMessage = this.editLastMessage.bind(this);
		this.closePrivateChat = this.closePrivateChat.bind(this);
		this.changeChat = this.changeChat.bind(this);
		this.composerChange = this.composerChange.bind(this);
		this.setRootElement = this.setRootElement.bind(this);
		this.takeComposerTextAsTitle = this.takeComposerTextAsTitle.bind(this);
	}

	/** "+ LFG" uses what is typed so far as the card's title, and empties the composer. */
	takeComposerTextAsTitle() {
		const title = this.getText().trim();
		this.setText('');
		return title;
	}

	componentDidMount() {
		this.mounted = true;
		if(!this.state.ComposerEditor) {
			loadComposerEditor().then((ComposerEditor) => {
				if(this.mounted) {
					this.setState({ ComposerEditor });
				}
			}, (error) => console.error(error));
		}
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	getText() {
		return this.composer.current ? this.composer.current.getText() : this.state.inputValue;
	}

	setText(text) {
		if(this.composer.current) {
			this.composer.current.setText(text);
		}
		else {
			this.composerChange(text);
		}
	}

	// Before the editor loads, inserted text goes on the end of the draft.
	appendToDraft(text) {
		this.composerChange(this.state.inputValue + text);
	}

	// The editable element attaches after componentDidMount, so the first
	// focus waits for it.
	setRootElement(rootElement) {
		this.chatInputReference = rootElement;
		if(!rootElement) {
			return;
		}
		this.props.chatMessages.setChatInputReference(rootElement);
		if(this.props.shouldFocusOnMount && !this.focusedOnMount) {
			this.focusedOnMount = true;
			this.focusInput();
		}
	}

	composerChange(text) {
		if(text === this.state.inputValue) {
			return;
		}
		this.setState({ inputValue: text });
		if(this.isTyping && text === '') {
			this.isTyping = false;
			this.sendChatState(false);
		}
		else if(!this.isTyping && text !== '') {
			this.isTyping = true;
			this.sendChatState(true);
		}
	}

	editLastMessage() {
		const { setChatMessageToEditMode, chatMessages } = this.props;
		let chosenMessage = null;
		for(let [_, message] of chatMessages.messages) {
			if(message.player && message.player.isMe() && message.type === ChatMessageNotification.types.CHAT_MESSAGE && !message.kind) {
				chosenMessage = message;
			}
		}
		setChatMessageToEditMode(chosenMessage);
	}

	closePrivateChat() {
		const { chatMessages } = this.props;
		if(!chatMessages.privateChatBox) {
			return false;
		}
		chatMessages.privateChatBox.close();
		return true;
	}

	changeChat(toTheLeft) {
		const { chatMessages } = this.props;
		if(toTheLeft) {
			chatMessages.changeChatLeft();
		}
		else {
			chatMessages.changeChatRight();
		}
	}

	toggleEmojiPicker(e) {
		e.preventDefault();
		this.composer.current?.saveSelection();
		this.setState((prevState) => {
			return {
				emojiPickerOpen: !prevState.emojiPickerOpen
			};
		});
	}

	closeEmojiPicker() {
		this.setState({ emojiPickerOpen: false }, () => {
			this.chatInputReference?.focus({ preventScroll: true });
		});
	}

	addEmoji(emoji) {
		this.setState({ emojiPickerOpen: false });
		if(!this.composer.current) {
			this.appendToDraft(emoji.native);
			return;
		}
		this.composer.current.insertEmoji(emoji.native);
		this.chatInputReference.focus({ preventScroll: true });
		this.composer.current.focus();
	}

	addMention(user) {
		// Called by a chat message's menu
		if(!this.composer.current) {
			this.appendToDraft(`@${user.username} `);
			return;
		}
		this.composer.current.insertMention(user.username);
		this.focusInput();
	}

	performSendChatAction() {
		const result = this.props.chatMessages.send.call(this.props.chatMessages, this.getText());
		if(result === true) {
			this.setText('');
			this.isTyping = false;
		}
	}

	focusInput() {
		const { isMobile } = this.props;
		if(isMobile || !this.chatInputReference) {
			return;
		}
		this.chatInputReference.focus({ preventScroll: true });
		this.composer.current?.focus();
	}

	componentDidUpdate() {
		this.props.chatMessages.componentState = { inputValue: this.state.inputValue };
	}

	placeholderText() {
		const { chatMessages, privateChat, isMobile } = this.props;
		if(privateChat) {
			if(privateChat.isBlocked) {
				return `${privateChat.player.username} ${__('is blocked!')}`;
			}
			return `${__('Message')} ${privateChat.player.username || __('... a person that is still loading ...')}${isMobile ? '' : __(' - ESC to close')}`;
		}
		if(chatMessages.chatRoom) {
			const chatRoom = chatMessages.chatRoom;
			return chatRoom.name ? __('Post a Message to') + ' ' + chatRoom.name : null;
		}
		if(chatMessages.match) {
			const players = chatMessages.match.getOtherPlayers().map((player) => player.username);
			return __('Message - ') + (players.length ? players.join(' ') : __('... Yourself'));
		}
		return null;
	}

	render() {
		const { privateChat, chatMessages, isDisabled, isMobile, canBeBottomAttached, worksOffNetplay, myChatRooms } = this.props;
		const { emojiPickerOpen, ComposerEditor } = this.state;

		const buttonInputProps = {};
		// Chat sends only from the netplay page, unless the host says this chat
		// works anywhere (the match dock's sheet: react/dock/MatchSheet).
		const chatInputIsDisabled = isDisabled || (!worksOffNetplay && !isNetplayPageActive());
		if(chatInputIsDisabled) {
			buttonInputProps.disabled = 'disabled';
		}
		const placeholder = this.placeholderText();
		return (
			<React.Fragment>
				<ChatPremadeResponsesComponent
					chatMessages={chatMessages}
				/>
				<form onSubmit={event => { event.preventDefault(); if (!chatInputIsDisabled) this.performSendChatAction(); }} className={`chat_input_group ${(!isMobile && canBeBottomAttached) ? 'fatty_bottom_helper' : ''}`}>
					{emojiPickerOpen && !chatInputIsDisabled && <EmojiPicker onSelect={this.addEmoji} onClose={this.closeEmojiPicker} triggerRef={this.emojiTriggerRef} />}
					<div className='input_beauty_wrapper flex flex-row'>
						<div className='chat_input_holder'>
							{ComposerEditor ?
								<ComposerEditor
									composerRef={this.composer}
									initialText={this.state.inputValue}
									placeholder={placeholder}
									isEditable={!chatInputIsDisabled}
									myChatRooms={myChatRooms}
									chatMessages={chatMessages}
									onTextChange={this.composerChange}
									onRootElement={this.setRootElement}
									onResize={() => $(window).trigger('resize')}
									onSend={this.performSendChatAction}
									onEditLastMessage={this.editLastMessage}
									onEscape={this.closePrivateChat}
									onTab={this.changeChat}
								/> :
								// Laid out as the editor is, so nothing moves when it arrives.
								<div className='react-autosuggest__container'>
									<div className='chat_input' aria-label={placeholder || __('Message')}>
										{this.state.inputValue || <div className='chat_input_placeholder'>{placeholder}</div>}
									</div>
								</div>
							}
						</div>
						{!chatInputIsDisabled && !privateChat && !!chatMessages.chatRoom &&
						<LfgComposerButton chatMessages={chatMessages} takeTitle={this.takeComposerTextAsTitle} />
						}
						{!chatInputIsDisabled &&
						<button
							onClick={this.toggleEmojiPicker}
							type='button' ref={this.emojiTriggerRef}
							aria-label={__('Open emoji picker')} aria-expanded={emojiPickerOpen}
							className='emoji_picker'>
							<i className='fa fa-smile-o' aria-hidden='true' />
						</button>
						}
						<button {...buttonInputProps}
							onClick={this.performSendChatAction}
							className='btn send_chat_button'
							aria-label={__('Send')}
							type='button'>
							<i className='fa fa-send' aria-hidden='true' />
						</button>
					</div>
				</form>
			</React.Fragment>
		);
	}

	sendChatState(isTyping) {
		const match = this.props.chatMessages.match;
		if(!match) {
			return;
		}
		if(!match.lobby) {
			return;
		}
		const data = {};
		data.match_id = match.id;
		data.is_typing = isTyping;
		data.chat_room_id = match.lobby ? match.lobby.id : null;

		Request.apiv1('chat/send_typing_status', data);
	}
}

function mapStateToProps(state) {
	return {
		...state.chat,
		...state.windowSize,
	};
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({
		...chatActions,
	}, dispatch);
}


export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ChatInputGroupComponent);